import React from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useMetricsData, useAdminOrders } from '../../lib/admin-hooks';
import { useTableMetrics } from '../../lib/table-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/utils';
import { TrendingUp, TrendingDown, DollarSign, Receipt, QrCode, User, Armchair, Percent } from 'lucide-react';
export function MetricsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    profitability,
    loading: metricsLoading
  } = useMetricsData(restaurant?.id);
  const {
    orders,
    loading: ordersLoading
  } = useAdminOrders(restaurant?.id);
  const {
    metrics: tableMetrics,
    loading: tableMetricsLoading
  } = useTableMetrics(restaurant?.id);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-1169">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-1170" />
      </div>;
  }
  const completedOrders = orders.filter(o => o.status === 'delivered' || o.status === 'ready');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total, 0);
  const averageTicket = completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1171">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-1172">
        <div data-id="element-1173">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1174">
            Métricas y Rentabilidad
          </h1>
          <p className="text-gray-500" data-id="element-1175">Análisis financiero de tu menú</p>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1176">
          <Card data-id="element-1177">
            <CardContent className="p-6" data-id="element-1178">
              <div className="flex items-center justify-between mb-4" data-id="element-1179">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1180">
                  <DollarSign className="w-5 h-5 text-emerald-600" data-id="element-1181" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1182">
                Ingresos (Recientes)
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1183">
                {formatCurrency(totalRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1184">
            <CardContent className="p-6" data-id="element-1185">
              <div className="flex items-center justify-between mb-4" data-id="element-1186">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1187">
                  <Receipt className="w-5 h-5 text-blue-600" data-id="element-1188" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1189">
                Ticket Promedio
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1190">
                {formatCurrency(averageTicket)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-1191">
            <CardContent className="p-6" data-id="element-1192">
              <div className="flex items-center justify-between mb-4" data-id="element-1193">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-1194">
                  <TrendingUp className="w-5 h-5 text-purple-600" data-id="element-1195" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-1196">
                Margen Promedio Menú
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1197">
                {profitability.length > 0 ? Math.round(profitability.reduce((sum, p) => sum + p.margin_percent, 0) / profitability.length) : 0}
                %
              </p>
            </CardContent>
          </Card>
        </div>

        {/* QR vs Waiter Metrics */}
        <div data-id="element-1198">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1199">
            Autoatención vs Mesero (Hoy)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1200">
            <Card data-id="element-1201">
              <CardContent className="p-6" data-id="element-1202">
                <div className="flex items-center justify-between mb-4" data-id="element-1203">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1204">
                    <QrCode className="w-5 h-5 text-emerald-600" data-id="element-1205" />
                  </div>
                  {tableMetrics.selfServicePercent > 0 && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-1206">
                      {tableMetrics.selfServicePercent}%
                    </span>}
                </div>
                <p className="text-sm font-medium text-gray-500" data-id="element-1207">
                  Pedidos por QR
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1208">
                  {tableMetricsLoading ? '—' : tableMetrics.qrOrdersToday}
                </p>
              </CardContent>
            </Card>

            <Card data-id="element-1209">
              <CardContent className="p-6" data-id="element-1210">
                <div className="flex items-center justify-between mb-4" data-id="element-1211">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1212">
                    <User className="w-5 h-5 text-blue-600" data-id="element-1213" />
                  </div>
                  {tableMetrics.waiterPercent > 0 && <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full" data-id="element-1214">
                      {tableMetrics.waiterPercent}%
                    </span>}
                </div>
                <p className="text-sm font-medium text-gray-500" data-id="element-1215">
                  Pedidos por Mesero
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1216">
                  {tableMetricsLoading ? '—' : tableMetrics.waiterOrdersToday}
                </p>
              </CardContent>
            </Card>

            <Card data-id="element-1217">
              <CardContent className="p-6" data-id="element-1218">
                <div className="flex items-center justify-between mb-4" data-id="element-1219">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-1220">
                    <Armchair className="w-5 h-5 text-amber-600" data-id="element-1221" />
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-500" data-id="element-1222">
                  Mesas Activas
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1223">
                  {tableMetricsLoading ? '—' : `${tableMetrics.activeTables} / ${tableMetrics.totalTables}`}
                </p>
              </CardContent>
            </Card>

            <Card data-id="element-1224">
              <CardContent className="p-6" data-id="element-1225">
                <div className="flex items-center justify-between mb-4" data-id="element-1226">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-1227">
                    <Receipt className="w-5 h-5 text-purple-600" data-id="element-1228" />
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-500" data-id="element-1229">
                  Ticket Promedio / Mesa
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-1230">
                  {tableMetricsLoading ? '—' : formatCurrency(tableMetrics.averageTicketPerTable)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Profitability Table */}
        <Card data-id="element-1231">
          <CardHeader data-id="element-1232">
            <CardTitle data-id="element-1233">Rentabilidad por Producto</CardTitle>
          </CardHeader>
          <CardContent data-id="element-1234">
            {metricsLoading ? <div className="space-y-4" data-id="element-1235">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-1236" />)}
              </div> : profitability.length > 0 ? <div className="overflow-x-auto" data-id="element-1237">
                <table className="w-full text-sm text-left" data-id="element-1238">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-1239">
                    <tr data-id="element-1240">
                      <th className="px-4 py-3 rounded-l-lg" data-id="element-1241">Producto</th>
                      <th className="px-4 py-3" data-id="element-1242">Categoría</th>
                      <th className="px-4 py-3 text-right" data-id="element-1243">Precio Venta</th>
                      <th className="px-4 py-3 text-right" data-id="element-1244">Costo Estimado</th>
                      <th className="px-4 py-3 text-right" data-id="element-1245">Ganancia Neta</th>
                      <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-1246">
                        Margen
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1247">
                    {profitability.map(item => {
                  const isGoodMargin = item.margin_percent >= 60;
                  const isBadMargin = item.margin_percent < 30;
                  return <tr key={item.menu_item_id} className="border-b border-gray-50 last:border-0" data-id="element-1248">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1249">
                            {item.item_name}
                          </td>
                          <td className="px-4 py-3 text-gray-500" data-id="element-1250">
                            {item.category || '—'}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-1251">
                            {formatCurrency(item.price)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-1252">
                            {formatCurrency(item.estimated_cost)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-emerald-600" data-id="element-1253">
                            {formatCurrency(item.estimated_margin)}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-1254">
                            <div className="flex items-center justify-end gap-2" data-id="element-1255">
                              <span className={`font-bold ${isGoodMargin ? 'text-emerald-600' : isBadMargin ? 'text-red-600' : 'text-amber-600'}`} data-id="element-1256">
                                {Math.round(item.margin_percent)}%
                              </span>
                              {isGoodMargin ? <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-1257" /> : isBadMargin ? <TrendingDown className="w-4 h-4 text-red-500" data-id="element-1258" /> : null}
                            </div>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-1259">
                No hay datos de rentabilidad disponibles. Asegúrate de
                configurar los costos en el inventario.
              </div>}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>;
}